import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { IconButton, List, ListItem, ListItemSecondaryAction, ListItemText } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { useEffect, useState } from 'react';
import { AgendaComponentInterface, AgendaEntry, SpeakerEntry, StageComponentInterface } from '../../../../../interfaces/pagebuilder';
import { useSelector } from '../../../../../reduxToolkit/typedRedux';
import { addAgendaEntry, deleteAgendaEntry, getSpeakersForEvent, updateAgendaEntry } from '../../../../../services/services';
import VyButton from '../../../../common/VyButton';
import { AgendaEditForm } from './AgendaEditForm';

interface EntryFormProps {
  component: AgendaComponentInterface | StageComponentInterface;
  onChange: (entryList: AgendaEntry[]) => void;
}
const EntrySettingsForm = ({ component, onChange }: EntryFormProps) => {
  const [editingEntry, setEditingEntry] = useState<AgendaEntry>();
  const [availableSpeakers, setAvailableSpeakers] = useState<SpeakerEntry[]>();
  const [isCreateModal, setIsCreateModal] = useState(false);
  const event = useSelector((state) => state.common.event);

  const { t } = useTranslation();

  async function createEntry(props: AgendaEntry) {
    const newComp = { ...component };
    const newEntry = await addAgendaEntry(props, component._id);
    newComp.entryList = [...(newComp.entryList || []), newEntry];
    onChange(newComp.entryList);
    setIsCreateModal(false);
  }

  async function deleteEntry(entryid: string) {
    await deleteAgendaEntry(entryid);
    const newComp = { ...component };
    newComp.entryList = newComp?.entryList?.filter((entry) => entry._id !== entryid);
    onChange(newComp.entryList || []);
  }

  async function editEntry(props: AgendaEntry) {
    const newComp = { ...component };
    if (!editingEntry || !editingEntry?._id || !newComp?.entryList) return;

    newComp.entryList = [...newComp.entryList];
    const entryIndex = editingEntry ? newComp.entryList.indexOf(editingEntry) : -1;
    newComp.entryList[entryIndex] = { ...newComp.entryList[entryIndex], ...props };
    await updateAgendaEntry(editingEntry?._id, props);
    setEditingEntry(undefined);
    onChange(newComp.entryList);
  }

  async function fetchSpeakers() {
    if (!event) return;
    const speakers = await getSpeakersForEvent(event._id);
    setAvailableSpeakers(speakers);
  }

  useEffect(() => {
    fetchSpeakers();
  }, [event]);

  const sortedEntries = [...(component?.entryList || [])].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
  return (
    <div>
      <VyButton isAddButton className="w-full" onClick={() => setIsCreateModal(true)}>
        {t('pagebuilder:entry-add')}
      </VyButton>
      <List className="mt-4" dense>
        {sortedEntries.map((entry) => (
          <ListItem>
            <ListItemText primary={entry.title?.substr(0, 50)} className="[overflow-wrap:anywhere] hyphens-auto" />
            <ListItemSecondaryAction>
              <IconButton onClick={() => setEditingEntry(entry)} edge="end" aria-label="delete" size="large">
                <EditIcon />
              </IconButton>

              <IconButton onClick={() => entry._id && deleteEntry(entry._id)} edge="end" aria-label="delete" size="large">
                <DeleteIcon />
              </IconButton>
            </ListItemSecondaryAction>
          </ListItem>
        ))}
      </List>
      {isCreateModal && (
        <AgendaEditForm
          isModal
          isModalOpen={isCreateModal}
          onSubmit={createEntry}
          onCloseModal={() => setIsCreateModal(false)}
          availableSpeakers={availableSpeakers}
        />
      )}
      {editingEntry && (
        <AgendaEditForm
          isModal
          isModalOpen={!!editingEntry}
          selectedEntry={editingEntry}
          onSubmit={editEntry}
          onCloseModal={() => setEditingEntry(undefined)}
          availableSpeakers={availableSpeakers}
        />
      )}
    </div>
  );
};
export default EntrySettingsForm;
